import { _notNil } from '@/littledash';
import type { AgentSessionStatus, AgentStreamEventType, AgentStreamEventV1 } from '@/model/Agent.model';
import { TERMINAL_SESSION_STATUSES } from '@/model/Agent.model';

/**
 * The stream event types the Activity feed ingests (v1: tool calls + thinking). Excludes text_delta
 * (high-frequency streaming text) and subagent_* (deferred — rendering them reliably needs a
 * server-issued subagent_call_id, the same pattern as tool_call_id). The stream wiring subscribes the
 * transport to exactly these and feeds them to the activity engine; anything else is ignored.
 */
export const ACTIVITY_EVENT_TYPES = [
  'tool_call_start',
  'tool_call_end',
  'thinking',
] as const satisfies ReadonlyArray<AgentStreamEventType>;

/**
 * A single Activity row. Tool-call rows transition running → done (matched end) or resolved (session
 * went terminal with no end); thinking rows carry the thought text. `key` is a stable React key:
 * `tool_call_id` for tool rows, the event id for thinking rows.
 */
export type ActivityRow =
  | { kind: 'tool'; key: string; toolName: string; state: 'running' | 'done' | 'resolved' }
  | { kind: 'thinking'; key: string; text: string };

/** Ordered rows keyed by their stable React key. Map insertion order == first-appearance order. */
export type ActivityRowMap = Map<string, ActivityRow>;

/**
 * Folds a single stream event into the accumulating row map, in place, and reports whether it
 * actually changed anything (so callers can skip re-notifying subscribers on a no-op). This is the one
 * place event → row logic lives; {@link AgentActivityEngine} applies events through it.
 *
 * Tool calls key on `tool_call_id` (start → running, matched end → done); thinking keys on the event
 * id. A `Map` re-set keeps the key's original position, so ordering needs no parallel index. Terminal
 * resolution (running → resolved) is NOT applied here — it depends on session status and is a final
 * pass in {@link toActivitySnapshot}, so the stored map only ever holds running/done.
 *
 * v1 handles tool calls + thinking only. text_delta (high-frequency streaming text) and subagent_*
 * are deferred — subagent rows need a server-issued subagent_call_id, the same pattern as tool_call_id.
 */
export const applyActivityEvent = (rows: ActivityRowMap, event: AgentStreamEventV1): boolean => {
  switch (event.type) {
    case 'tool_call_start': {
      const key = `tool:${event.tool_call_id}`;
      if (rows.has(key)) {
        return false; // idempotent: a duplicate start is a no-op
      }
      rows.set(key, { kind: 'tool', key, toolName: event.tool_name, state: 'running' });
      return true;
    }
    case 'tool_call_end': {
      const key = `tool:${event.tool_call_id}`;
      const existing = rows.get(key);
      if (_notNil(existing) && existing.kind === 'tool' && existing.state !== 'done') {
        rows.set(key, { ...existing, state: 'done' });
        return true;
      }
      return false;
    }
    case 'thinking': {
      const key = `think:${event.id ?? ''}`;
      const existing = rows.get(key);
      const text = event.content ?? '';
      if (existing?.kind === 'thinking' && existing.text === text) {
        return false;
      }
      rows.set(key, { kind: 'thinking', key, text });
      return true;
    }
    default:
      return false; // other stream types don't produce Activity rows
  }
};

/**
 * Materialises the accumulated rows into the ordered array the panel renders, applying the one
 * status-dependent transform: when the session is terminal, any tool call still `running` (its end
 * never arrived) becomes `resolved` — a neutral "ended" state, not a false tick. The stored map is
 * left untouched (resolution is derived, not persisted), so it stays correct if more events arrive.
 */
export const toActivitySnapshot = (rows: ActivityRowMap, terminal: boolean): Array<ActivityRow> =>
  [...rows.values()].map((row) =>
    terminal && row.kind === 'tool' && row.state === 'running' ? { ...row, state: 'resolved' } : row
  );

/** Whether a session status means the run has finished (drives terminal row resolution). */
export const isTerminalStatus = (status: AgentSessionStatus | undefined): boolean =>
  _notNil(status) && TERMINAL_SESSION_STATUSES.has(status);
